import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { VideoProviderSettingsRequester } from '../Services/VideoProviderSettings.requester';
import {
	InstructorUsageRow,
	PagedPayload,
	UpdateVideoProviderPayload,
	UsageMeta,
	VideoProviderContainer,
	VideoProviderSetting,
} from '../Types/VideoProviders.types';

export type VideoProviderSettingsState = {
	providers: VideoProviderSetting[];
	containers: { list: VideoProviderContainer[]; meta: ResponseMeta | null };
	usage: { list: InstructorUsageRow[]; meta: UsageMeta | null };
	loaders: {
		fetch?: StateLoader;
		update?: StateLoader;
		containers?: StateLoader;
		usage?: StateLoader;
		applyTemplate?: StateLoader;
	};
	errors: {
		fetch?: string | null;
		update?: string | null;
		containers?: string | null;
		usage?: string | null;
		applyTemplate?: string | null;
	};
};

const initialState: VideoProviderSettingsState = {
	providers: [],
	containers: { list: [], meta: null },
	usage: { list: [], meta: null },
	loaders: {},
	errors: {},
};

export const videoProviderErrorMessage = (error: any, fallback: string) =>
	error?.response?.data?.message ??
	error?.response?.data?.errors?.[0]?.message ??
	fallback;

const requester = VideoProviderSettingsRequester.getInstance();

export const videoProvidersFetch = createAsyncThunk(
	'VideoProviderSettings/fetch',
	async (_: void, { rejectWithValue }) => {
		try {
			const { data } = await requester.getAll();
			return data;
		} catch (error) {
			return rejectWithValue(error);
		}
	}
);

export const videoProvidersUpdate = createAsyncThunk(
	'VideoProviderSettings/update',
	async (payload: UpdateVideoProviderPayload, { rejectWithValue }) => {
		try {
			const { data } = await requester.update(payload);
			return data;
		} catch (error) {
			return rejectWithValue(error);
		}
	}
);

export const videoProvidersContainersFetch = createAsyncThunk(
	'VideoProviderSettings/containers',
	async (payload: PagedPayload, { rejectWithValue }) => {
		try {
			const { data } = await requester.containers(payload);
			return data;
		} catch (error) {
			return rejectWithValue(error);
		}
	}
);

export const videoProvidersUsageFetch = createAsyncThunk(
	'VideoProviderSettings/usage',
	async (payload: PagedPayload, { rejectWithValue }) => {
		try {
			const { data } = await requester.usage(payload);
			return data;
		} catch (error) {
			return rejectWithValue(error);
		}
	}
);

export const videoProvidersApplyBunnyTemplate = createAsyncThunk(
	'VideoProviderSettings/applyBunnyTemplate',
	async (_: void, { rejectWithValue }) => {
		try {
			const { data } = await requester.applyBunnyTemplate();
			return data;
		} catch (error) {
			return rejectWithValue(error);
		}
	}
);

const videoProviderSettingsSlice = createSlice({
	name: 'VideoProviderSettings',
	initialState,
	reducers: {},
	extraReducers: (builder) => {
		builder
			.addCase(videoProvidersFetch.pending, (state) => {
				state.loaders.fetch = 'pending';
				state.errors.fetch = null;
			})
			.addCase(videoProvidersFetch.fulfilled, (state, { payload }) => {
				state.providers = payload.data.providers;
				state.loaders.fetch = 'fulfilled';
			})
			.addCase(videoProvidersFetch.rejected, (state, action) => {
				state.loaders.fetch = 'rejected';
				state.errors.fetch = videoProviderErrorMessage(
					action.payload,
					"Couldn't load video provider settings"
				);
			});
		builder
			.addCase(videoProvidersUpdate.pending, (state) => {
				state.loaders.update = 'pending';
				state.errors.update = null;
			})
			.addCase(videoProvidersUpdate.fulfilled, (state, { payload }) => {
				// PUT echoes the full masked list back.
				state.providers = payload.data.providers;
				state.loaders.update = 'fulfilled';
			})
			.addCase(videoProvidersUpdate.rejected, (state, action) => {
				state.loaders.update = 'rejected';
				state.errors.update = videoProviderErrorMessage(
					action.payload,
					"Couldn't save video provider settings"
				);
			});
		builder
			.addCase(videoProvidersContainersFetch.pending, (state) => {
				state.loaders.containers = 'pending';
				state.errors.containers = null;
			})
			.addCase(videoProvidersContainersFetch.fulfilled, (state, { payload }) => {
				state.containers.list = payload.data.containers;
				state.containers.meta = payload.meta;
				state.loaders.containers = 'fulfilled';
			})
			.addCase(videoProvidersContainersFetch.rejected, (state, action) => {
				state.loaders.containers = 'rejected';
				state.errors.containers = videoProviderErrorMessage(
					action.payload,
					"Couldn't load provider containers"
				);
			});
		builder
			.addCase(videoProvidersUsageFetch.pending, (state) => {
				state.loaders.usage = 'pending';
				state.errors.usage = null;
			})
			.addCase(videoProvidersUsageFetch.fulfilled, (state, { payload }) => {
				state.usage.list = payload.data.usage;
				state.usage.meta = payload.meta;
				state.loaders.usage = 'fulfilled';
			})
			.addCase(videoProvidersUsageFetch.rejected, (state, action) => {
				state.loaders.usage = 'rejected';
				state.errors.usage = videoProviderErrorMessage(
					action.payload,
					"Couldn't load provider usage"
				);
			});
		builder
			.addCase(videoProvidersApplyBunnyTemplate.pending, (state) => {
				state.loaders.applyTemplate = 'pending';
				state.errors.applyTemplate = null;
			})
			.addCase(videoProvidersApplyBunnyTemplate.fulfilled, (state) => {
				state.loaders.applyTemplate = 'fulfilled';
			})
			.addCase(videoProvidersApplyBunnyTemplate.rejected, (state, action) => {
				state.loaders.applyTemplate = 'rejected';
				state.errors.applyTemplate = videoProviderErrorMessage(
					action.payload,
					"Couldn't apply the library template"
				);
			});
	},
});

export default videoProviderSettingsSlice.reducer;
